import {
    v2,
    FRAGMENTNAMEPART,
    PropertyName,
    ControlType,
    PageTypeV2,
    PAGETYPE_VIEW_EXTENSION_TEMPLATE_MAP,
    ViewTemplateType,
    LogSeverity,
    ManifestSection,
    DefinitionName
} from '@sap/ux-specification-types';
import type {
    ChangeDefinition,
    FileData,
    ObjectPageSectionData,
    Manifest,
    ExtensionLogger,
    CommonImportParameters
} from '@sap/ux-specification-types';
import {
    convertActionKeyToStableId,
    checkDeprecatedProperties,
    importProperty,
    parseChangeString
} from '../../../common';
import type { MetadataInstanceInterface } from '../../../common/decoration/factory';
import { getReflectMetadata } from '../../../common/decoration/decorators';
import { ensureManifestSectionByPathV2 } from '../../export/manifest';
import { log } from '../../../../extensionLogger';
import i18next from 'i18next';
import { xml2json } from 'xml-js';
import type { TableCustomColumn } from '../../export/controls';
import { FRAGMENT_DEFINITION, getRootCustomColumnFromFragmentJSON } from '../../export/controls';
import { ActionTypeV2 } from '../../types';
import type { Definition } from 'typescript-json-schema';
import { importFlexChangesOfColumn } from '../controls/table';

export type LROPConfigsType = v2.ListReportConfigV2 | v2.AnalyticalListPageConfigV2 | v2.ObjectPageConfigV2;

/**
 * Converts an action property key by replacing '/' with '::'.
 *
 * @param actionProperty - The action property key to be converted.
 * @returns The converted action property key.
 */
export function convertActionKey(actionProperty: string) {
    return actionProperty.replace('/', '::');
}

/**
 * Unescapes the selector ID of a flex change.
 *
 * @param selectorId - escaped selector ID
 * @returns unescaped ID
 */
export function convertSelector(selectorId: string): string {
    return selectorId.replace(/:2f/g, '/').replace(/:23/g, '#').replace(/:40/g, '@');
}

/**
 * Adds flex changes to Smart Filter Bar in config file.
 *
 * @param config - the configuration to be filled
 * @param changePropertyKey - property key of current change
 * @param change - change definition
 * @param jsonSchema - application specific schema
 */
function addSmartFilterBarFlex(
    config: v2.ListReportConfigV2 | v2.AnalyticalListPageConfigV2,
    changePropertyKey: string,
    change: ChangeDefinition,
    jsonSchema: object
) {
    const schemaProperty =
        jsonSchema['definitions']?.['ListReportFilterBar']?.properties[changePropertyKey] ||
        jsonSchema['definitions']?.['AnalyticalListPageFilterBar']?.properties[changePropertyKey];
    if (schemaProperty) {
        config.filterBar[changePropertyKey] = change.content.newBinding
            ? change.content.newBinding
            : change.content.newValue;
    }
}

/**
 * Creates a map to connect action types to the correct schema definition and config part.
 *
 * @param definitionType action type that will be filtered for
 * @param jsonSchema application specific schema
 * @returns {Map} actionMap - mapping of actionId, id for schema reference and id for config
 */
function filterActions(definitionType: string, jsonSchema) {
    const actions = Object.keys(jsonSchema['definitions']).filter(
        (definition) => definition.startsWith(`${definitionType}`) && definition.includes('<')
    );
    const actionMap: Map<string, string> = new Map();
    actions.forEach((definitionActionId) => {
        const fullActionId = convertActionKeyToStableId(definitionActionId.split('<')[1]?.split('>')[0]);
        const splitPoint = fullActionId.indexOf(':::');
        if (fullActionId.startsWith('DataField') && splitPoint > -1) {
            const actionIdParts = fullActionId.split(':::');
            const sSemanticObject = actionIdParts.find((part) => part.startsWith('sSemanticObject'));
            const sAction = actionIdParts.find((part) => part.startsWith('sAction'));
            const shortAction = sAction.split('sAction::')[1].replace(':2f', '::');
            if (sSemanticObject) {
                const shortActionId = `${sSemanticObject.split('::')[1]}::${shortAction}`;
                const shortDefinitionActionId = `${actionIdParts[0]}:::${sSemanticObject}:::${sAction}`;
                actionMap.set(shortActionId, definitionActionId);
                actionMap.set(definitionActionId, shortDefinitionActionId);
            } else {
                actionMap.set(shortAction, definitionActionId);
                actionMap.set(definitionActionId, fullActionId);
            }
        } else {
            actionMap.set(fullActionId.replace(':2f', '::'), definitionActionId);
            actionMap.set(definitionActionId, fullActionId);
        }
    });
    return actionMap;
}

/**
 * Adds a new action column to config.
 *
 * @param action - action part of config to be filled
 * @param action.name - The name of the action type.
 * @param action.configPart - The part of the configuration where the action details will be added.
 * @param action.id - The unique identifier for the action.
 * @param config - the config of ALP/LR
 * @param configId - id of action to be used in config
 */
function addColumnActionConfigPart(
    action: { name: ActionTypeV2; configPart: v2.Actions | v2.GenericColumns | v2.HeaderActionsStandard; id: string },
    config: v2.ListReportConfigV2 | v2.AnalyticalListPageConfigV2,
    configId: string
) {
    if (!action.configPart) {
        action.configPart = config.table.columns = {};
    }
    if (!action.configPart[`${configId}`]) {
        action.configPart[`${configId}`] = {};
    }
}

/**
 * Return action id of change.
 *
 * @param {ChangeDefinition} change - change definition
 * @returns {string} action id
 */
function getActionId(change: ChangeDefinition): string | undefined {
    if (change.controlId?.includes('--template::')) {
        return change.controlId.split('--template::')[1];
    }
    return change.controlId?.split('--action::')[1];
}

/**
 * Adds flex changes to Actions in config file.
 *
 * @param config - the configuration to be filled
 * @param changePropertyKey - property key of current change
 * @param change - change definition
 * @param jsonSchema - application specific schema
 * @param id - The unique identifier for the action.
 */
function addActionFlex(
    config: v2.ListReportConfigV2 | v2.AnalyticalListPageConfigV2,
    changePropertyKey: string,
    change: ChangeDefinition,
    jsonSchema: Definition,
    id: string
) {
    const actionTypes = [
        { name: ActionTypeV2.HeaderStandard, configPart: config.header.actions, id },
        { name: ActionTypeV2.OPHeaderStandard, configPart: config.header.actions, id },
        {
            name: ActionTypeV2.Footer,
            configPart: config.footer.actions,
            id: id && id.includes('Determining') && id.split('::Determining')[0]
        },
        { name: ActionTypeV2.OPFooterStandard, configPart: config.footer.actions, id },
        { name: ActionTypeV2.ToolBar, configPart: config.table.toolBar.actions, id },
        { name: ActionTypeV2.Column, configPart: config.table.columns, id }
    ];
    actionTypes.forEach((action) => {
        const schemaActions = filterActions(action.name, jsonSchema);
        if (schemaActions.has(action.id)) {
            const referenceId = schemaActions.get(action.id);
            const configId = referenceId.split('<')[1]?.split('>')[0];
            if (action.name === ActionTypeV2.Column) {
                addColumnActionConfigPart(action, config, configId);
            }
            const configPart = action.configPart[configId];
            const schemaProperty = (jsonSchema.definitions[referenceId] as Definition)?.properties[changePropertyKey];
            if (schemaProperty && configPart) {
                configPart[changePropertyKey] = change.content.newBinding
                    ? change.content.newBinding
                    : change.content.newValue;
            }
        }
    });
}

/**
 * Adds flex changes to Smart Chart in config file.
 *
 * @param config - the configuration to be filled
 * @param changePropertyKey - property key of current change
 * @param change - change definition
 * @param jsonSchema - application specific schema
 */
function addSmartChartFlex(
    config: v2.ListReportConfigV2 | v2.AnalyticalListPageConfigV2,
    changePropertyKey: string,
    change: ChangeDefinition,
    jsonSchema: object
) {
    const schemaProperty = jsonSchema['definitions']['ChartSettings'].properties[changePropertyKey];
    if (schemaProperty) {
        const syncRule = getReflectMetadata(config[PropertyName.chart], changePropertyKey);
        if (syncRule?.flex?.importFlexValue) {
            syncRule.flex.importFlexValue(change, config[PropertyName.chart], changePropertyKey);
        } else {
            config[PropertyName.chart][changePropertyKey] = change.content.newBinding
                ? change.content.newBinding
                : change.content.newValue;
        }
    }
}

/**
 * Adds flex changes to config file.
 *
 * @param config - the configuration to be filled
 * @param changeString - change file as string
 * @param appSchema - application specific schema
 * @param logger - Logger class for logging messages
 */
export function addFlex(
    config: v2.ListReportConfigV2 | v2.AnalyticalListPageConfigV2,
    changeString: string,
    appSchema: object,
    logger?: ExtensionLogger
): void {
    try {
        const change = parseChangeString(changeString) as ChangeDefinition;
        const changePropertyKey = change.content.property;
        if (
            change.controlId?.includes(PageTypeV2.ListReport) ||
            change.controlId?.includes(PageTypeV2.AnalyticalListPage)
        ) {
            change.controlId = convertSelector(change.controlId);
            switch (change.controlType) {
                case ControlType.SmartFilterBar: {
                    addSmartFilterBarFlex(config, changePropertyKey, change, appSchema);
                    break;
                }
                case ControlType.DynamicPage: {
                    const schemaProperty = appSchema['properties'][changePropertyKey];
                    if (schemaProperty) {
                        config[change.content.property] = change.content.newValue;
                    }
                    break;
                }
                case ControlType.Button: {
                    const actionId = getActionId(change);
                    if (actionId) {
                        addActionFlex(config, changePropertyKey, change, appSchema, actionId);
                    }
                    break;
                }
                case ControlType.SmartChart: {
                    addSmartChartFlex(config, changePropertyKey, change, appSchema);
                    break;
                }
                default: {
                    break;
                }
            }
        }
    } catch (error) {
        log(
            logger,
            {
                severity: LogSeverity.Error,
                message: i18next.t('FLEXERROR')
            },
            ...error
        );
    }
}

/**
 * Special post-processing for quickVariantSelectionX during the import.
 *
 * @param propertyKey - key of the current schema property
 * @param configPart  - part of the configuration to be enhanced
 */
function adjustQuickVariantSelectionX(propertyKey: string, configPart: { [key: string]: any }) {
    if (propertyKey === 'quickVariantSelectionX' && configPart[propertyKey]) {
        for (const variant in configPart[propertyKey]['variants']) {
            const currentQuickVariantX = configPart[propertyKey]['variants'][variant];
            if (currentQuickVariantX['tableSettings']) {
                for (const key in currentQuickVariantX['tableSettings']) {
                    currentQuickVariantX[key] = currentQuickVariantX['tableSettings'][key];
                }
                delete currentQuickVariantX['tableSettings'];
            }
        }
    }
}

/**
 * Transfers settings from manifest to object page config.
 *
 * @param configPart - part of the configuration to be filled
 * @param manifest - manifest.json of the app
 * @param appSchema - app specific schema
 * @param pageKey - The key of the page in the manifest.
 * @param logger - Logger class for logging messages.
 * @param kpiKey - The key of the KPI, if applicable.
 */
export function transferSettingsOfObject(
    configPart: { [key: string]: any },
    manifest: Manifest,
    appSchema: object,
    pageKey: string,
    logger?: ExtensionLogger,
    kpiKey?: string
): void {
    if (!appSchema) {
        return;
    }
    let manifestSection = {};
    const deprecatedProperties = configPart.getDeprecated ? configPart.getDeprecated() : undefined;

    if (appSchema['properties']) {
        for (const propertyKey in appSchema['properties']) {
            const syncRule = getReflectMetadata(configPart, propertyKey);
            if (syncRule?.manifest) {
                const path = syncRule.manifest.path([pageKey], manifest, kpiKey);
                manifestSection = ensureManifestSectionByPathV2(manifest, path);
                importProperty(syncRule, manifestSection, propertyKey, configPart);
                checkDeprecatedProperties(deprecatedProperties, manifestSection, path, logger);
            }
            // Adjustment of 'quickVariantSelectionX' - tableSettings
            adjustQuickVariantSelectionX(propertyKey, configPart);
        }
    }
}

/**
 * Determines the table type for a given LR or ALP table, based on the component name and its table settings
 * This does not exactly reflect the run time behavior of Fiori elements but a best guess and expectation.
 *
 * @param manifest - manifest.json file content of the app
 * @param pageKey - key of the page in the manifest
 * @returns - the determined table type
 */
export function determineTableType(manifest: Manifest, pageKey: string): v2.TableTypeV2 {
    const page = manifest[ManifestSection.generic].pages[pageKey];
    let tableType: v2.TableTypeV2 = v2.TableTypeV2.ResponsiveTable;
    if (
        manifest[ManifestSection.generic].pages[pageKey].component?.settings &&
        (manifest[ManifestSection.generic].pages[pageKey].component.settings?.tableSettings?.['type'] ||
            manifest[ManifestSection.generic].pages[pageKey].component.settings?.['tableType'])
    ) {
        tableType = page.component?.settings?.tableSettings?.['type'] || page.component?.settings['tableType'];
    } else if (page.component?.name === 'sap.suite.ui.generic.template.AnalyticalListPage') {
        tableType = v2.TableTypeV2.AnalyticalTable;
    }
    return tableType;
}

/**
 * Adds custom column information from manifest and fragments to the configuration.
 *
 * @param column - The column object containing metadata and attributes.
 * @param viewExtensionsOfPage - The view extensions of the page as specified in the manifest.
 * @param manifestKey - The key in the manifest associated with the view extension.
 * @param keyPart3 - The third part of the key, typically representing a tab or section.
 * @param config - The configuration object to be updated with column information.
 * @param extensionType - The type of the table column extension.
 * @param cellsFragmentName - The name of the fragment for the table cells, if applicable.
 */
function evaluateFragmentColumn(
    column: any,
    viewExtensionsOfPage: any,
    manifestKey: string,
    keyPart3: string,
    config: v2.ListReportConfigV2,
    extensionType: v2.TableColumnExtensionTypeV2,
    cellsFragmentName?: string
): void {
    if (column['customData'] || column['table:customData']) {
        const p13nData = column['customData']
            ? column['customData']['core:CustomData']._attributes.value
            : column['table:customData']['core:CustomData']._attributes.value;
        const columnParts = JSON.parse(p13nData.substr(1, p13nData.length));
        let columnText = column['Text'] && column['Text']._attributes && column['Text']._attributes['text'];
        if (!columnText) {
            columnText = column['Label'] && column['Label']._attributes && column['Label']._attributes['text'];
        }
        const customElement: TableCustomColumn = {
            ...(column._attributes?.['id'] ? { id: column._attributes?.['id'] } : {}),
            ...(columnText ? { text: columnText } : {}),
            ...columnParts,
            ...(columnParts['columnKey'] ? { columnKey: columnParts['columnKey'] } : {}),
            ...(columnParts['columnIndex'] ? { columnIndex: Number(columnParts['columnIndex']) } : {}),
            ...(columnParts['leadingProperty'] ? { leadingProperty: columnParts['leadingProperty'] } : {}),
            className: viewExtensionsOfPage[manifestKey].className,
            fragmentName: viewExtensionsOfPage[manifestKey].fragmentName,
            ...(keyPart3 ? { tabKey: keyPart3 } : {}),
            extensionType
        };
        if (cellsFragmentName) {
            customElement.cellsFragmentName = cellsFragmentName;
        }
        if (!config.table['columns']['custom']) {
            config.table['columns']['custom'] = [];
        }
        const customColumns = config.table['columns']['custom'] as v2.TableCustomColumns;
        customColumns.push(customElement);
    }
}

/**
 * Determines the fragment name for cells based on the extension type and view extensions of the page.
 *
 * @param extensionType - The type of the extension (e.g., ResponsiveTableColumnsExtension).
 * @param viewExtensionsOfPage - The view extensions of the page as specified in the manifest.
 * @param keyEntitySet - The entity set key associated with the extension.
 * @param referenceId - The reference ID for the tab or section.
 * @returns The name of the cells fragment, if applicable.
 */
function determineCellsFragmentName(
    extensionType: any,
    viewExtensionsOfPage: any,
    keyEntitySet: string,
    referenceId: string
) {
    let cellsFragmentName;
    if (extensionType === ViewTemplateType.ResponsiveTableColumnsExtension) {
        for (const cellsKey of Object.keys(viewExtensionsOfPage)) {
            const [cellsKeyType, cellsKeyEntitySet, cellsKeyTab] = cellsKey.split('|');
            if (
                cellsKeyType === ViewTemplateType.ResponsiveTableCellsExtension &&
                cellsKeyEntitySet === keyEntitySet &&
                cellsKeyTab === referenceId
            ) {
                cellsFragmentName = viewExtensionsOfPage[cellsKey].fragmentName;
            }
        }
    }
    return cellsFragmentName;
}

/**
 * Analyzes the filtered fragments containing the XML content to be analyzed.
 *
 * @param filteredFragments - The filtered fragments containing the XML content to be analyzed.
 * @param config - The configuration object to be updated with column information.
 * @param viewExtensionsOfPage - The view extensions of the page as specified in the manifest.
 * @param manifestKey - The key in the manifest associated with the view extension.
 * @param keyTab - The tab key, typically representing a specific section or tab.
 * @param extensionType - The type of the table column extension.
 * @param cellsFragmentName - The name of the fragment for the table cells, if applicable.
 */
function analyzeFilteredFragments(
    filteredFragments: FileData[],
    config: LROPConfigsType,
    viewExtensionsOfPage: any,
    manifestKey: string,
    keyTab: string,
    extensionType: any,
    cellsFragmentName: any
) {
    if (filteredFragments[0]) {
        const fragmentJson = JSON.parse(xml2json(filteredFragments[0].fileContent, { compact: true }));
        const fragmentDefinition = fragmentJson[FRAGMENT_DEFINITION];
        try {
            const columnDefinition = getRootCustomColumnFromFragmentJSON(fragmentDefinition);
            if (columnDefinition) {
                if (!config[PropertyName.table]?.['columns']) {
                    config[PropertyName.table]['columns'] = {};
                }
                if (Array.isArray(columnDefinition)) {
                    for (const def of columnDefinition) {
                        evaluateFragmentColumn(
                            def,
                            viewExtensionsOfPage,
                            manifestKey,
                            keyTab,
                            config,
                            extensionType,
                            cellsFragmentName
                        );
                    }
                } else {
                    evaluateFragmentColumn(
                        columnDefinition,
                        viewExtensionsOfPage,
                        manifestKey,
                        keyTab,
                        config,
                        extensionType,
                        cellsFragmentName
                    );
                }
            }
        } catch {
            throw new Error(`Invalid fragment ${filteredFragments[0]['dataSourceUri']}, please check content`);
        }
    }
}

/**
 * Imports view extensions of a page.
 *
 * @param {object} viewExtensionsOfPage - the view extensions of a page, as specified in manifest
 * @param {string} targetKeyEntitySet - the entitySet, as part of the extension key
 * @param {LROPConfigsType} config - the configuration to be filled
 * @param {CommonImportParameters} importParameters  - object comprising all input data
 * @param lineItemsId - ID of the line items
 */
function processViewExtensions(
    viewExtensionsOfPage: object,
    targetKeyEntitySet: string,
    config: LROPConfigsType,
    importParameters: CommonImportParameters,
    lineItemsId: string
) {
    for (const extensionId of Object.keys(viewExtensionsOfPage)) {
        const extensionIdParts = extensionId.split('|');
        const [extensionType, entitySet] = extensionIdParts;

        const keyTab = extensionIdParts[2];
        // If Object Page sections have a tab key, then ignore it as it is not from QuickVariantSelectionX
        if (extensionType && entitySet === targetKeyEntitySet) {
            const fragmentType = viewExtensionsOfPage[extensionId].type
                ? viewExtensionsOfPage[extensionId].type.toLowerCase()
                : 'xml';
            let fragmentName = viewExtensionsOfPage[extensionId].fragmentName;
            if (!fragmentName) {
                continue;
            }
            // Responsive Table ==> check cells' fragment name
            const cellsFragmentName = determineCellsFragmentName(
                extensionType,
                viewExtensionsOfPage,
                entitySet,
                keyTab
            );

            // Retrieve information from fragment
            const fragmentParts = fragmentName.split('.');
            fragmentName = fragmentParts[fragmentParts.length - 1] + FRAGMENTNAMEPART + fragmentType;
            const filteredFragments = importParameters.fragments.filter((fragment) =>
                fragment['dataSourceUri'].endsWith(fragmentName)
            );
            try {
                analyzeFilteredFragments(
                    filteredFragments,
                    config,
                    viewExtensionsOfPage,
                    extensionId,
                    keyTab,
                    extensionType,
                    cellsFragmentName
                );
            } catch (error) {
                //invalid fragment?!
                importParameters.logger.error(error.message);
            }
            if (config['table']['columns']?.custom) {
                //custom array exists, it comprises the custom columns
                config['table']['columns'].custom.forEach((customColumn) => {
                    importFlexChangesOfColumn(importParameters, config, customColumn.id, customColumn, lineItemsId);
                });
            }
        }
    }
}

/**
 * Adds custom column information from manifest and fragments to the configuration.
 *
 * @param {LROPConfigsType} config - current configuration, to be enhanced
 * @param {CommonImportParameters} importParameters  - object comprising all input data
 * @param {v2.SapUiAppPageV2} v2Page - actual page in the manifest
 * @param {PageTypeV2} pageType - type of the page
 * @param objectPageSectionData - optional information, for object page
 * @param lineItemsId - ID of the line items
 */
export function addFragments(
    config: LROPConfigsType,
    importParameters: CommonImportParameters,
    v2Page: v2.SapUiAppPageV2,
    pageType: PageTypeV2,
    objectPageSectionData?: ObjectPageSectionData,
    lineItemsId = 'LineItems'
): void {
    if (!importParameters.fragments) {
        return;
    }
    if (importParameters.manifest[ManifestSection.ui5]?.extends?.extensions?.['sap.ui.viewExtensions']) {
        const viewExtension = PAGETYPE_VIEW_EXTENSION_TEMPLATE_MAP.get(pageType);
        const viewExtensionsOfPage =
            importParameters.manifest[ManifestSection.ui5].extends.extensions['sap.ui.viewExtensions'][viewExtension];
        if (!viewExtensionsOfPage) {
            return;
        }
        const targetKeyEntitySet =
            objectPageSectionData && objectPageSectionData.target ? objectPageSectionData.target : v2Page.entitySet;

        processViewExtensions(viewExtensionsOfPage, targetKeyEntitySet, config, importParameters, lineItemsId);
    }
}

/**
 * Prepares header standard actions information for LR/ALP config.
 *
 * @param listReportConfig - LR config, to be filled
 * @param factory - factory, for generating the access to reflect-metadata
 * @param schemaDefinitions - app schema definitions
 */
export function importHeaderActions(
    listReportConfig: v2.ListReportConfigV2,
    factory: MetadataInstanceInterface,
    schemaDefinitions: Definition
) {
    listReportConfig.header = { actions: {} };
    if (schemaDefinitions[DefinitionName.HeaderActionsStandard]) {
        const headerActions = schemaDefinitions[DefinitionName.HeaderActionsStandard].properties;
        for (const actionKey in headerActions) {
            listReportConfig.header.actions[actionKey] = factory.createInstance(PageTypeV2.ListReport, actionKey, {});
        }
    }
}
